import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Calendar, Clock, Phone,
  MessageSquare, Bell, RefreshCw, Wallet, BarChart3, Sparkles, Play, Loader2,
  ChevronRight, Check, Users, TrendingUp, Heart, Activity, Stethoscope, Target,
  Star, FileText, PhoneCall, Download, Smartphone
} from "lucide-react";
import SmartRecordsStep from "./demos/SmartRecordsStep";
import WellnessSuiteStep from "./demos/WellnessSuiteStep";

// ============================================================
// DEMO DATA (Fisioterapia / Osteopatia / Benessere Posturale)
// ============================================================
const OPERATORS = [
  { name: "Dr. Andrea Bianchi",     role: "Fisioterapista",    color: "bg-sky-500"      },
  { name: "Dr.ssa Chiara Rinaldi",  role: "Osteopata",         color: "bg-emerald-500"  },
  { name: "Dr. Matteo Ferri",       role: "Terapia manuale",   color: "bg-violet-500"   },
  { name: "Reception",              role: "Accoglienza",       color: "bg-slate-500"    },
];

const ROOMS = ["Sala 1", "Sala 2", "Sala Tecar"];

const APPOINTMENTS = [
  { time: "09:00", room: 0, op: 0, service: "Rieducazione posturale", patient: "Anna Russo",     color: "bg-sky-100 dark:bg-sky-900/40 border-sky-500"           },
  { time: "09:30", room: 1, op: 1, service: "Trattamento osteopatico", patient: "Luca Greco",     color: "bg-emerald-100 dark:bg-emerald-900/40 border-emerald-500" },
  { time: "10:00", room: 2, op: 0, service: "Tecarterapia",            patient: "Maria Costa",    color: "bg-amber-100 dark:bg-amber-900/40 border-amber-500"      },
  { time: "11:00", room: 0, op: 2, service: "Terapia manuale",         patient: "Giuseppe Neri",  color: "bg-violet-100 dark:bg-violet-900/40 border-violet-500"   },
  { time: "15:30", room: 1, op: 1, service: "Seduta osteopatica",      patient: "Sofia Bruno",    color: "bg-emerald-100 dark:bg-emerald-900/40 border-emerald-500" },
  { time: "16:30", room: 2, op: 0, service: "Rieducazione motoria",    patient: "Paolo Ferri",    color: "bg-sky-100 dark:bg-sky-900/40 border-sky-500"            },
];

const SERVICES = [
  { name: "Valutazione gratuita",           duration: 30, price: 0,   highlight: true },
  { name: "Seduta di fisioterapia",         duration: 50, price: 55 },
  { name: "Trattamento osteopatico",        duration: 50, price: 70 },
  { name: "Tecarterapia",                   duration: 30, price: 45 },
  { name: "Rieducazione posturale",         duration: 60, price: 65 },
  { name: "Terapia manuale",                duration: 45, price: 60 },
];

const PACKAGES = [
  { name: "Pacchetto 5 sedute Fisio",     sessions: 5,  price: 240, savings: 35 },
  { name: "Pacchetto 10 sedute Fisio",    sessions: 10, price: 440, savings: 110 },
  { name: "Rieducazione Posturale 8 sed.", sessions: 8,  price: 460, savings: 60 },
];

const INACTIVE_PATIENTS = [
  { name: "Mario Rossi",   last: "ultima seduta 4 mesi fa",   condition: "Lombalgia cronica",        avatar: "MR" },
  { name: "Elisa Greco",   last: "ciclo concluso 6 mesi fa",  condition: "Follow-up post-infortunio", avatar: "EG" },
  { name: "Luca Neri",     last: "ultima visita 5 mesi fa",   condition: "Cervicalgia ricorrente",    avatar: "LN" },
];

const TOTAL_STEPS = 12;

// ============================================================
// Main component
// ============================================================
const DemoBenesserePosturale = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sessionId] = useState(() => `fisio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const stepStartRef = useRef(Date.now());

  const track = async (event, extra = {}) => {
    try {
      await axios.post(`${API}/demo/events/track`, {
        session_id: sessionId,
        variant: "fisio",
        event,
        step,
        duration_ms: Date.now() - stepStartRef.current,
        data: extra,
      }).catch(() => {});
      // Also feed the unified landing analytics bucket
      axios.post(`${API}/public/landing/track`, {
        event_type: event, landing: "demo-benessere-posturale",
        session_id: sessionId, meta: { step, ...extra },
      }).catch(() => {});
    } catch {}
  };

  useEffect(() => {
    track("demo_started", { source: "page_load" });
    const onUnload = () => {
      navigator.sendBeacon?.(
        `${API}/demo/events/track`,
        new Blob([JSON.stringify({
          session_id: sessionId, variant: "fisio", event: "exit_step",
          step, duration_ms: Date.now() - stepStartRef.current,
        })], { type: "application/json" })
      );
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { stepStartRef.current = Date.now(); }, [step]);

  const next = () => {
    track("step_completed", { from_step: step });
    setStep(s => Math.min(s + 1, TOTAL_STEPS));
  };
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const clickTrial = async (source) => {
    await track("clicked_trial", { source, plan: "benessere_pro" });
    navigate("/onboarding?sector=fisioterapia&prefill=demo-benessere-posturale&plan=benessere_pro");
  };
  const clickCall = async (source) => {
    await track("clicked_call", { source });
  };

  const progress = (Math.min(step + 1, TOTAL_STEPS) / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="demo-fisio-page">
      {/* Sticky progress header */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/benessere-posturale" className="text-xs text-slate-500 hover:text-slate-900 transition-colors hidden sm:flex items-center gap-1" data-testid="demo-fisio-back">
            <ArrowLeft className="w-3 h-3" /> SmartBooker
          </Link>
          <div className="flex-1 flex items-center gap-3">
            <span className="text-xs text-slate-500 font-semibold whitespace-nowrap" data-testid="demo-fisio-step-label">
              {Math.min(step + 1, TOTAL_STEPS)}/{TOTAL_STEPS}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
                data-testid="demo-fisio-progress"
              />
            </div>
            <span className="text-[10px] text-slate-400 hidden md:inline">Demo completa in ~2 min</span>
          </div>
          <Button
            onClick={() => clickTrial("header")}
            className="rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white text-xs px-4 h-8"
            data-testid="demo-fisio-header-cta"
          >
            Prova gratis 14gg
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {step === 0 && <StepCover next={next} clickTrial={clickTrial} />}
        {step === 1 && <StepProblem next={next} />}
        {step === 2 && <WellnessSuiteStep />}
        {step === 3 && <SmartRecordsStep sector="fisio" onCta={() => clickTrial("smart_records_step")} />}
        {step === 4 && <StepCalendar next={next} />}
        {step === 5 && <StepBooking next={next} />}
        {step === 6 && <StepWhatsApp next={next} />}
        {step === 7 && <StepReminder next={next} />}
        {step === 8 && <StepRecall next={next} />}
        {step === 9 && <StepPackages next={next} />}
        {step === 10 && <StepAnalytics next={next} />}
        {step === 11 && <StepClosing clickTrial={clickTrial} clickCall={clickCall} />}

        {/* Nav */}
        {step > 0 && step < 10 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <Button
              variant="ghost"
              onClick={prev}
              className="text-slate-500 hover:text-slate-900 rounded-full"
              data-testid="demo-fisio-prev"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            <Button
              onClick={next}
              className="rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white shadow-lg"
              data-testid="demo-fisio-next"
            >
              {step === 10 ? "Vedi i risultati" : "Avanti"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// STEP 0 — Cover
// ============================================================
const StepCover = ({ next, clickTrial }) => (
  <div className="text-center py-8 sm:py-16 animate-[fadeIn_0.6s_ease]" data-testid="demo-fisio-step-0">
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-6">
      <Sparkles className="w-3 h-3" /> DEMO INTERATTIVA · Fisio & Osteopati
    </div>
    <h1 className="text-4xl sm:text-6xl font-black mb-6 leading-tight tracking-tight">
      Vedi con i tuoi occhi come<br />
      <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">
        raddoppi le sedute confermate
      </span>
    </h1>
    <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
      In <strong>2 minuti</strong> ti mostro <strong>tutto</strong> quello che SmartBooker fa per il tuo studio —
      dalla prima prenotazione online al paziente che torna per un nuovo ciclo.
    </p>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto mb-10" data-testid="demo-fisio-preview-grid">
      {[
        { icon: Calendar,      label: "Agenda", color: "from-sky-500 to-blue-600" },
        { icon: MessageSquare, label: "WhatsApp", color: "from-emerald-500 to-teal-600" },
        { icon: Bell,          label: "Reminder", color: "from-violet-500 to-purple-600" },
        { icon: RefreshCw,     label: "Recall",   color: "from-amber-500 to-orange-600" },
      ].map((c, i) => (
        <div key={i} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
          <div className={`w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center shadow-md`}>
            <c.icon className="w-5 h-5 text-white" />
          </div>
          <p className="text-xs font-semibold">{c.label}</p>
        </div>
      ))}
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center items-center max-w-md mx-auto">
      <Button
        onClick={next}
        size="lg"
        className="w-full sm:w-auto rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white px-8 h-12 shadow-xl"
        data-testid="demo-fisio-start"
      >
        <Play className="w-4 h-4 mr-2" /> Inizia la demo
      </Button>
      <Button
        onClick={() => clickTrial("cover")}
        size="lg"
        variant="outline"
        className="w-full sm:w-auto rounded-full border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-8 h-12"
        data-testid="demo-fisio-cover-trial"
      >
        Prova gratis 14 giorni →
      </Button>
    </div>

    <div className="mt-10 flex items-center justify-center gap-6 text-xs text-slate-500">
      <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Nessuna carta</span>
      <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Setup 3 minuti</span>
      <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Supporto in italiano</span>
    </div>
  </div>
);

// ============================================================
// STEP 1 — Problem
// ============================================================
const StepProblem = ({ next }) => (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-1">
    <div className="text-center mb-10">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold mb-4">
        <AlertTriangle className="w-3 h-3" /> 1/10 · Il problema che vivi ogni giorno
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Ogni settimana <span className="text-rose-600">perdi ore e soldi</span>
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Non è colpa tua. È un sistema frammentato che <strong>ti frena</strong> e ti ruba energie che dovresti dare ai pazienti.
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
      {[
        { icon: Phone,         text: "Rispondi al telefono mentre sei in seduta",              loss: "10-15 chiamate perse/giorno" },
        { icon: Clock,         text: "Pazienti che non si presentano senza avvisare",          loss: "3-5 no-show a settimana" },
        { icon: MessageSquare, text: "Conferme e promemoria scritti a mano uno per uno",       loss: "2 ore perse al giorno" },
        { icon: RefreshCw,     text: "Pazienti che finiscono il ciclo e non tornano più",      loss: "40% non torna per un nuovo ciclo" },
        { icon: FileText,      text: "Pacchetti sedute gestiti su carta o Excel",              loss: "Errori e crediti persi" },
        { icon: BarChart3,     text: "Nessun numero chiaro su ricavi, riempimento e no-show",  loss: "Decisioni a intuito" },
      ].map((p, i) => (
        <div
          key={i}
          className="p-4 rounded-2xl bg-rose-50 border border-rose-200"
          data-testid={`demo-fisio-pain-${i}`}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-rose-200 flex items-center justify-center flex-shrink-0">
              <p.icon className="w-4 h-4 text-rose-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-slate-900">{p.text}</p>
              <p className="text-xs text-rose-700 mt-1 font-medium">{p.loss}</p>
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="mt-10 text-center p-5 rounded-2xl bg-slate-900 text-white max-w-2xl mx-auto">
      <p className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-bold">Il conto finale</p>
      <p className="text-2xl font-black text-rose-400">~€8.000 di fatturato perso ogni anno</p>
      <p className="text-xs text-slate-400 mt-2">Stima media per uno studio da 3 sedute/settimana di no-show a €60</p>
    </div>
  </div>
);

// ============================================================
// STEP 2 — Calendar
// ============================================================
const StepCalendar = ({ next }) => {
  const [liveAppt, setLiveAppt] = useState(null);
  const [flash, setFlash] = useState(false);

  // Simulate a live booking coming in after 2.5s
  useEffect(() => {
    const t1 = setTimeout(() => {
      setLiveAppt({
        time: "14:00",
        room: 0,
        op: 1,
        service: "Seduta osteopatica",
        patient: "Elena Viola",
        color: "bg-emerald-100 dark:bg-emerald-900/40 border-emerald-500",
      });
      setFlash(true);
    }, 2500);
    const t2 = setTimeout(() => setFlash(false), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const timeRows = ["09:00", "09:30", "10:00", "11:00", "14:00", "15:30", "16:30"];

  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-2">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold mb-4">
        <Calendar className="w-3 h-3" /> 2/10 · La tua agenda, finalmente chiara
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Tutti gli operatori. Tutte le sale. <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">Un colpo d'occhio.</span>
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Vedi all'istante chi è libero, chi è occupato e dove sistemare un'urgenza — senza più mail, telefonate e post-it.
      </p>
    </div>

    <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-xl overflow-hidden max-w-5xl mx-auto relative">
      {/* Live booking banner */}
      <div
        className={`absolute top-14 right-4 z-20 transform transition-all duration-500 ${
          flash ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
        data-testid="demo-fisio-live-booking"
      >
        <div className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold shadow-xl flex items-center gap-2 animate-pulse">
          <span className="w-1.5 h-1.5 rounded-full bg-white" />
          📲 Nuova prenotazione online · Elena Viola · 14:00
        </div>
      </div>

      <div className="bg-slate-50 p-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-slate-700">Lunedì 12 Maggio</span>
        <span className="text-xs text-slate-500">· 3 operatori · 3 sale</span>
        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE · {liveAppt ? "99%" : "98%"} riempimento
        </span>
      </div>
      <div className="p-3 sm:p-4 overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-4 gap-2 mb-2">
            <div className="text-xs text-slate-400 font-semibold">Ora</div>
            {ROOMS.map((r, i) => (
              <div key={i} className="text-xs font-bold text-slate-700">{r}</div>
            ))}
          </div>
          {timeRows.map(time => (
            <div key={time} className="grid grid-cols-4 gap-2 mb-1.5 items-stretch">
              <div className="text-xs text-slate-500 font-semibold py-2">{time}</div>
              {[0, 1, 2].map(room => {
                // Check both static + live bookings
                const fromStatic = APPOINTMENTS.find(a => a.time === time && a.room === room);
                const fromLive = liveAppt && liveAppt.time === time && liveAppt.room === room ? liveAppt : null;
                const appt = fromStatic || fromLive;
                if (!appt) return <div key={room} className="h-12 rounded-lg border border-dashed border-slate-200 bg-slate-50/50" />;
                const op = OPERATORS[appt.op];
                const isLive = !!fromLive;
                return (
                  <div
                    key={room}
                    className={`p-2 rounded-lg border-l-4 ${appt.color} text-[11px] transition-all ${
                      isLive ? "animate-[fadeInScale_0.5s_ease] ring-2 ring-emerald-400 ring-offset-1 shadow-lg" : ""
                    }`}
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${op.color}`} />
                      <span className="text-slate-500 text-[10px]">{op.role}</span>
                      {isLive && (
                        <span className="ml-auto text-[8px] font-bold text-emerald-700 uppercase tracking-wider">NEW</span>
                      )}
                    </div>
                    <p className="font-bold text-slate-900 truncate">{appt.service}</p>
                    <p className="text-slate-600 truncate">{appt.patient}</p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-emerald-50 border-t border-emerald-200 p-3 text-xs text-emerald-800 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" />
        <span className="font-semibold">Drag & drop</span> per spostare una seduta, <span className="font-semibold">click</span> per aprire la scheda del paziente.
      </div>
    </div>

    <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-4xl mx-auto">
      {[
        { icon: Users, title: "Multi-operatore", text: "Ogni fisio/osteo ha la sua colonna" },
        { icon: Stethoscope, title: "Multi-sala", text: "Evita doppi impegni sulle stanze" },
        { icon: Clock, title: "Conflitti risolti", text: "Il sistema blocca sovrapposizioni" },
      ].map((f, i) => (
        <div key={i} className="p-4 rounded-2xl bg-white border border-slate-200">
          <f.icon className="w-5 h-5 text-sky-600 mb-2" />
          <h4 className="font-bold text-sm">{f.title}</h4>
          <p className="text-xs text-slate-600 mt-1">{f.text}</p>
        </div>
      ))}
    </div>
  </div>
  );
};

// ============================================================
// STEP 3 — Booking online (Smart Page)
// ============================================================
const StepBooking = ({ next }) => (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-3">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
        <Target className="w-3 h-3" /> 3/10 · Prenotazioni online 24/7
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Il tuo studio prenotabile <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">anche di notte</span>
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Ogni studio riceve una <strong>Smart Page pubblica</strong> professionale: i pazienti scelgono trattamento, giorno e orario — tu ricevi tutto già in agenda.
      </p>
    </div>

    <div className="max-w-4xl mx-auto bg-white rounded-3xl border-2 border-slate-200 shadow-xl overflow-hidden">
      {/* Fake URL bar */}
      <div className="bg-slate-100 p-2 border-b border-slate-200 flex items-center gap-2">
        <div className="flex gap-1">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
        <div className="flex-1 text-xs font-mono text-slate-500 bg-white rounded px-2 py-0.5 border border-slate-200 truncate">
          smartbooker.it/studio-benessere-milano
        </div>
      </div>
      <div className="p-5 sm:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 items-center justify-center mb-3 shadow-lg">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold">Studio Benessere Milano</h3>
          <p className="text-xs text-slate-500">Fisioterapia · Osteopatia · Rieducazione posturale</p>
          <div className="flex items-center justify-center gap-1 mt-2">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
            <span className="text-xs text-slate-500 ml-1">4.9 · 128 recensioni</span>
          </div>
        </div>

        <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Scegli il trattamento</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-5">
          {SERVICES.slice(0, 4).map((s, i) => (
            <div
              key={i}
              className={`p-3 rounded-xl border-2 ${s.highlight ? "border-emerald-500 bg-emerald-50" : "border-slate-200"} flex items-center justify-between`}
              data-testid={`demo-fisio-service-${i}`}
            >
              <div>
                <p className="font-bold text-sm">{s.name}</p>
                <p className="text-xs text-slate-500">{s.duration} minuti</p>
              </div>
              <div className="text-right">
                <p className="font-black text-sm">
                  {s.price === 0
                    ? <span className="text-emerald-600">GRATIS</span>
                    : `€${s.price}`}
                </p>
              </div>
            </div>
          ))}
        </div>

        <Button className="w-full rounded-xl bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white h-12 font-bold shadow-lg">
          Prenota seduta <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>

    <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-4xl mx-auto">
      {[
        { title: "Prenotabile 24/7",           text: "Anche mentre dormi o sei in seduta" },
        { title: "Incassi al momento",         text: "Pagamento con Stripe, carta, PayPal" },
        { title: "Zero chiamate perse",        text: "La maggior parte dei pazienti prenota fuori orario" },
      ].map((f, i) => (
        <div key={i} className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
          <h4 className="font-bold text-sm">{f.title}</h4>
          <p className="text-xs text-slate-600 mt-1">{f.text}</p>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// STEP 4 — WhatsApp booking confirmation
// ============================================================
const StepWhatsApp = ({ next }) => {
  const [stage, setStage] = useState(0);
  // Stages:
  //  0 = studio typing
  //  1 = studio message shown
  //  2 = patient typing
  //  3 = patient reply shown
  //  4 = done (loop after 1s pause)
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      while (mounted) {
        setStage(0); await new Promise(r => setTimeout(r, 1400));
        if (!mounted) return; setStage(1); await new Promise(r => setTimeout(r, 2400));
        if (!mounted) return; setStage(2); await new Promise(r => setTimeout(r, 1100));
        if (!mounted) return; setStage(3); await new Promise(r => setTimeout(r, 3500));
        if (!mounted) return; setStage(4); await new Promise(r => setTimeout(r, 600));
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  return (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-4">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
        <MessageSquare className="w-3 h-3" /> 4/10 · WhatsApp automatico
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Conferma istantanea. <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">Dove il paziente la legge davvero.</span>
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Appena un paziente prenota, riceve un messaggio <strong>WhatsApp</strong> di conferma personalizzato — non una mail che finisce nello spam.
      </p>
    </div>

    <div className="max-w-md mx-auto">
      <div className="bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-t-3xl p-4 text-white flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <Heart className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="font-bold text-sm">Studio Benessere Milano</p>
          <p className="text-[10px] opacity-80">
            {stage === 0 ? "sta scrivendo..." : "online"}
          </p>
        </div>
      </div>
      <div className="bg-[#e5ddd5] dark:bg-slate-800 p-4 space-y-2 min-h-[360px]">
        {/* Studio typing indicator */}
        {stage === 0 && (
          <div className="bg-white rounded-2xl rounded-tl-none p-3 w-16 shadow-sm" data-testid="demo-fisio-wa-typing-studio">
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}

        {/* Studio confirmed message */}
        {stage >= 1 && (
          <div className="bg-white rounded-2xl rounded-tl-none p-3 max-w-[85%] shadow-sm animate-[fadeInScale_0.4s_ease]" data-testid="demo-fisio-wa-msg">
            <p className="text-sm text-slate-900 leading-relaxed">
              ✅ <strong>Ciao Anna!</strong><br /><br />
              La tua seduta è confermata:<br />
              📅 <strong>Lun 12 Maggio · 15:30</strong><br />
              💆 Rieducazione posturale — 60 min<br />
              👨‍⚕️ Dr. Andrea Bianchi<br /><br />
              📍 Via Roma 12, Milano<br /><br />
              Se hai bisogno di modificare, rispondi qui.
            </p>
            <p className="text-[10px] text-slate-400 text-right mt-1">09:42 ✓✓</p>
          </div>
        )}

        {/* Patient typing */}
        {stage === 2 && (
          <div className="flex justify-end">
            <div className="bg-emerald-200 rounded-2xl rounded-tr-none p-3 w-16 shadow-sm" data-testid="demo-fisio-wa-typing-patient">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-700/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 rounded-full bg-emerald-700/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 rounded-full bg-emerald-700/60 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}

        {/* Patient reply */}
        {stage >= 3 && (
          <div className="flex justify-end animate-[fadeInScale_0.4s_ease]">
            <div className="bg-emerald-200 rounded-2xl rounded-tr-none p-3 max-w-[80%] shadow-sm" data-testid="demo-fisio-wa-reply">
              <p className="text-sm text-slate-900">Grazie! 🙏 A lunedì</p>
              <p className="text-[10px] text-slate-500 text-right mt-1">09:43 ✓✓</p>
            </div>
          </div>
        )}
      </div>
      <div className="bg-white rounded-b-3xl border border-t-0 border-slate-200 p-3 text-center">
        <p className="text-xs text-slate-600">
          <span className="font-bold text-emerald-600">+ 32% tasso di conferma</span> rispetto alla sola email
        </p>
      </div>
    </div>

    <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-4xl mx-auto">
      {[
        { title: "Tasso apertura 98%",  text: "Contro il 20% delle email" },
        { title: "Personalizzato",      text: "Nome, trattamento, operatore, indirizzo" },
        { title: "Due vie",             text: "Il paziente può rispondere direttamente" },
      ].map((f, i) => (
        <div key={i} className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
          <MessageSquare className="w-5 h-5 text-emerald-600 mb-2" />
          <h4 className="font-bold text-sm">{f.title}</h4>
          <p className="text-xs text-slate-600 mt-1">{f.text}</p>
        </div>
      ))}
    </div>
  </div>
  );
};

// ============================================================
// STEP 5 — Reminder (reduce no-show)
// ============================================================
const StepReminder = ({ next }) => (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-5">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold mb-4">
        <Bell className="w-3 h-3" /> 5/10 · Reminder automatici
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        <span className="text-rose-600">-70% no-show</span>. Senza muovere un dito.
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        24 e 2 ore prima della seduta, SmartBooker manda <strong>promemoria automatici</strong> via WhatsApp.
        Il paziente può confermare o spostare con un tap.
      </p>
    </div>

    <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-4">
      {[
        { hours: 24, title: "24h prima", color: "from-sky-500 to-blue-600",
          msg: "🔔 Ciao Luca, ti ricordo la tua seduta di domani alle 10:00 con il Dr. Ferri. Conferma con un tap 👇", cta: "Confermo ✅  /  Sposta 🔄" },
        { hours:  2, title: "2h prima",  color: "from-violet-500 to-purple-600",
          msg: "⏰ Tra 2 ore ti aspettiamo in via Roma 12. Porta con te abbigliamento comodo. A dopo!", cta: "Vedi mappa 📍" },
      ].map((r, i) => (
        <div key={i} className="rounded-3xl overflow-hidden border-2 border-slate-200 bg-white shadow-lg" data-testid={`demo-fisio-reminder-${i}`}>
          <div className={`bg-gradient-to-r ${r.color} p-3 text-white`}>
            <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Reminder</p>
            <p className="font-bold">{r.title}</p>
          </div>
          <div className="p-4">
            <div className="bg-[#e5ddd5] rounded-2xl p-3 mb-3">
              <p className="text-sm text-slate-900 leading-relaxed">{r.msg}</p>
            </div>
            <p className="text-xs text-slate-500 text-center">{r.cta}</p>
          </div>
        </div>
      ))}
    </div>

    <div className="mt-8 max-w-3xl mx-auto p-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-center">
      <p className="text-xs uppercase tracking-wider opacity-80 mb-2 font-bold">Risultato medio dei nostri studi</p>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-3xl font-black">-70%</p>
          <p className="text-xs opacity-80">No-show</p>
        </div>
        <div>
          <p className="text-3xl font-black">+32%</p>
          <p className="text-xs opacity-80">Conferme</p>
        </div>
        <div>
          <p className="text-3xl font-black">€600</p>
          <p className="text-xs opacity-80">Recuperati/mese</p>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================
// STEP 6 — Recall pazienti dormienti
// ============================================================
const StepRecall = ({ next }) => (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-6">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold mb-4">
        <RefreshCw className="w-3 h-3" /> 6/10 · Recall pazienti dormienti
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Riattivi chi non torna <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">senza chiamare nessuno</span>
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Il 40% dei pazienti finisce il ciclo e <strong>non torna più</strong>. SmartBooker li riattiva in automatico con messaggi personalizzati ogni 3 o 6 mesi.
      </p>
    </div>

    <div className="max-w-3xl mx-auto bg-white rounded-3xl border-2 border-slate-200 shadow-xl overflow-hidden">
      <div className="bg-amber-50 border-b border-amber-200 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-bold">Campagna recall attiva</p>
          <p className="text-xs text-slate-600">Pazienti senza sedute da oltre 3 mesi</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-2xl font-black text-amber-600">{INACTIVE_PATIENTS.length}</p>
          <p className="text-[10px] uppercase text-slate-500">in coda</p>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {INACTIVE_PATIENTS.map((p, i) => (
          <div key={i} className="p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`demo-fisio-recall-${i}`}>
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center text-white font-bold text-sm shadow">
              {p.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{p.name}</p>
              <p className="text-xs text-slate-500 truncate">{p.condition}</p>
              <p className="text-[11px] text-amber-700 mt-0.5 font-medium">{p.last}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Messaggio inviato</span>
              <Check className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
        ))}
      </div>
      <div className="bg-slate-50 border-t border-slate-200 p-4">
        <p className="text-xs text-slate-600 text-center">
          Ogni paziente riceve un messaggio <strong>personalizzato</strong> sulla sua condizione. Il 25% torna a prenotare entro 2 settimane.
        </p>
      </div>
    </div>

    <div className="mt-6 max-w-2xl mx-auto p-4 rounded-2xl bg-slate-900 text-white text-center">
      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1 font-bold">Valore medio recuperato</p>
      <p className="text-2xl font-black text-emerald-400">+€1.200/mese</p>
      <p className="text-xs text-slate-400 mt-1">solo dai pazienti dormienti riattivati</p>
    </div>
  </div>
);

// ============================================================
// STEP 7 — Packages (pacchetti sedute)
// ============================================================
const StepPackages = ({ next }) => (
  <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-7">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold mb-4">
        <Wallet className="w-3 h-3" /> 7/10 · Pacchetti sedute
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">
        Vendi <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">pacchetti in un click</span>, traccia ogni credito
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Crei pacchetti da 5, 8, 10 sedute. SmartBooker gestisce il saldo automaticamente e ti avvisa quando il paziente è al <strong>penultimo credito</strong> — così vendi il rinnovo al momento giusto.
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
      {PACKAGES.map((pkg, i) => (
        <div
          key={i}
          className={`p-5 rounded-2xl border-2 ${i === 1 ? "border-emerald-500 bg-emerald-50 shadow-xl scale-105" : "border-slate-200 bg-white"}`}
          data-testid={`demo-fisio-pkg-${i}`}
        >
          {i === 1 && (
            <span className="inline-block mb-3 text-[10px] uppercase tracking-wider font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full">
              Il più Acquistato
            </span>
          )}
          <h3 className="font-bold text-lg mb-2">{pkg.name}</h3>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-black">€{pkg.price}</span>
            <span className="text-xs text-slate-400 line-through">€{pkg.price + pkg.savings}</span>
          </div>
          <p className="text-xs text-emerald-600 font-bold mb-4">Risparmio: €{pkg.savings}</p>
          <div className="space-y-1.5">
            {[
              `${pkg.sessions} sedute complete`,
              "Prenotabili quando vuoi",
              "Validità 6 mesi",
              "Saldo visibile in tempo reale",
            ].map((f, j) => (
              <div key={j} className="flex items-center gap-2 text-xs">
                <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                <span className="text-slate-700">{f}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className="mt-8 max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Dashboard paziente · saldo pacchetto</p>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold shadow">
          LR
        </div>
        <div className="flex-1">
          <p className="font-bold">Laura Ricci · Pacchetto 10 Fisio</p>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500" style={{ width: "80%" }} />
          </div>
          <p className="text-xs text-slate-500 mt-1.5">8 sedute usate · <span className="text-amber-600 font-bold">2 rimaste</span></p>
        </div>
        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
          Proponi rinnovo
        </Button>
      </div>
    </div>
  </div>
);

// ============================================================
// STEP 8 — Analytics / ROI
// ============================================================
const StepAnalytics = ({ next }) => {
  const kpis = [
    { icon: Users,       label: "Pazienti attivi",        value: "284",    trend: "+18%",  color: "from-sky-500 to-blue-600"         },
    { icon: Calendar,    label: "Sedute erogate",         value: "512",    trend: "+24%",  color: "from-emerald-500 to-teal-600"     },
    { icon: Wallet,      label: "Fatturato del mese",     value: "€14.8k", trend: "+31%",  color: "from-violet-500 to-purple-600"    },
    { icon: TrendingUp,  label: "Riempimento agenda",     value: "92%",    trend: "+15pt", color: "from-amber-500 to-orange-600"     },
    { icon: RefreshCw,   label: "Recall riattivati",      value: "23",     trend: "+€1.2k",color: "from-rose-500 to-pink-600"        },
    { icon: Bell,        label: "No-show",                value: "3%",     trend: "-70%",  color: "from-indigo-500 to-blue-600"      },
  ];
  return (
    <div className="animate-[fadeIn_0.5s_ease]" data-testid="demo-fisio-step-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold mb-4">
          <BarChart3 className="w-3 h-3" /> 8/10 · I tuoi numeri, finalmente chiari
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold mb-3">
          Decidi sui <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">dati</span>, non a intuito
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto">
          Fatturato, riempimento agenda, no-show, recall riattivati, trattamenti più redditizi — tutto nella stessa dashboard, aggiornato in tempo reale.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-5xl mx-auto">
        {kpis.map((k, i) => (
          <div key={i} className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid={`demo-fisio-kpi-${i}`}>
            <div className="flex items-start justify-between mb-2">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${k.color} flex items-center justify-center shadow`}>
                <k.icon className="w-5 h-5 text-white" />
              </div>
              <span className="text-[10px] font-bold text-emerald-600 px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200">
                {k.trend}
              </span>
            </div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{k.label}</p>
            <p className="text-2xl font-black text-slate-900">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 max-w-3xl mx-auto p-6 rounded-3xl bg-gradient-to-br from-sky-600 via-sky-700 to-emerald-700 text-white text-center shadow-2xl">
        <p className="text-xs uppercase tracking-wider opacity-80 mb-2 font-bold">In 3 mesi con SmartBooker, uno studio medio guadagna</p>
        <p className="text-5xl font-black mb-2">+€9.500</p>
        <p className="text-sm opacity-90">
          recuperando no-show, riattivando pazienti dormienti e vendendo più pacchetti
        </p>
      </div>
    </div>
  );
};

// ============================================================
// STEP 9 — Closing / Conversion CTA
// ============================================================
const StepClosing = ({ clickTrial, clickCall }) => (
  <div className="animate-[fadeIn_0.5s_ease] py-6 sm:py-10" data-testid="demo-fisio-step-9">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold mb-4">
        <Sparkles className="w-3 h-3" /> Fine demo · Pronto a partire?
      </div>
      <h2 className="text-4xl sm:text-5xl font-black mb-4 leading-tight">
        Prova SmartBooker <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">14 giorni gratis</span>
      </h2>
      <p className="text-lg text-slate-600 max-w-2xl mx-auto">
        Setup guidato in <strong>3 minuti</strong>. Nessuna carta. Cancellazione istantanea.
      </p>
    </div>

    <div className="max-w-2xl mx-auto bg-white rounded-3xl border-2 border-emerald-200 p-6 sm:p-8 shadow-xl">
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {[
          "Agenda multi-operatore e multi-sala",
          "Prenotazioni online 24/7",
          "Reminder WhatsApp automatici",
          "Pacchetti sedute con saldo",
          "Recall pazienti dormienti",
          "Analytics e report mensili",
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <Check className="w-3 h-3 text-white" />
            </div>
            <span className="text-slate-700">{f}</span>
          </div>
        ))}
      </div>

      <Button
        onClick={() => clickTrial("closing")}
        size="lg"
        className="w-full rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white h-14 text-base font-bold shadow-xl"
        data-testid="demo-fisio-closing-trial"
      >
        Inizia la prova gratuita di 14 giorni <ArrowRight className="w-5 h-5 ml-2" />
      </Button>

      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Nessuna carta</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Setup 3 min</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Supporto ITA</span>
      </div>

      <div className="mt-6 pt-6 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-500 mb-3">Preferisci una chiamata di 15 minuti con un consulente?</p>
        <a
          href="tel:+390000000000"
          onClick={() => clickCall("closing")}
          className="inline-flex items-center gap-2 text-sm font-bold text-sky-600 hover:text-sky-700"
          data-testid="demo-fisio-closing-call"
        >
          <PhoneCall className="w-4 h-4" /> Prenota una demo telefonica
        </a>
      </div>
    </div>

    {/* Patient Portal LIVE demo — preview from your phone */}
    <a
      href="/portal/demo-portal-marco-rossi-recovery-2026"
      target="_blank" rel="noreferrer"
      className="mt-6 max-w-2xl mx-auto block group rounded-3xl bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-300 hover:border-teal-500 hover:shadow-xl transition-all p-5"
      data-testid="demo-fisio-portal-live"
    >
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-teal-500/30">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-700">Patient Portal · LIVE demo</p>
          <h3 className="text-base font-black text-slate-900 mt-0.5 group-hover:text-teal-700 transition">Apri l'esperienza paziente sul tuo smartphone</h3>
          <p className="text-xs text-slate-600 mt-1">
            Marco Rossi · 6 settimane di trattamento · Recovery Score 50/100 · pain 9→4 · 4/10 sedute residue
          </p>
        </div>
        <ArrowRight className="w-5 h-5 text-teal-600 group-hover:translate-x-1 transition flex-shrink-0" />
      </div>
    </a>

    {/* Brochure download — premium PDF */}
    <div className="mt-8 max-w-2xl mx-auto rounded-3xl bg-gradient-to-br from-teal-900 via-slate-900 to-emerald-900 text-white p-6 sm:p-8 shadow-2xl border border-teal-500/30 relative overflow-hidden" data-testid="demo-fisio-brochure-block">
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-teal-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-teal-500/40">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-300">Brochure PDF · 7 pagine premium</p>
            <h3 className="text-xl font-black mt-1">Scarica la brochure per fisio e osteopati</h3>
            <p className="text-sm text-slate-300 mt-2">
              Tutta la Recovery Suite spiegata in 7 pagine A4 stampabili. Perfetta da condividere
              con colleghi, partner o il commercialista per parlare di SmartBooker Wellness.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <a
            href={`${API}/downloads/wellness-brochure.pdf`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400 text-slate-900 text-sm font-black hover:opacity-95 transition"
            data-testid="demo-fisio-brochure-download"
          >
            <Download className="w-4 h-4" /> Scarica PDF gratis
          </a>
          <button
            onClick={() => {
              const url = `${window.location.origin}/api/downloads/wellness-brochure.pdf`;
              const text = `Ho appena scoperto SmartBooker Wellness: la piattaforma per fisioterapisti e osteopati. Brochure premium qui 👉 ${url}`;
              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-teal-400/40 text-teal-200 text-sm font-bold hover:bg-teal-500/10 transition"
            data-testid="demo-fisio-brochure-wa"
          >
            <MessageSquare className="w-4 h-4" /> Condividi su WhatsApp
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-3">PDF A4 · 485 KB · stampabile · senza watermark · condivisibile</p>
      </div>
    </div>

    <div className="mt-10 flex flex-wrap items-center justify-center gap-6 max-w-3xl mx-auto">
      {[
        { label: "Studi attivi",       value: "120+"  },
        { label: "Sedute gestite",     value: "50.000+" },
        { label: "Pazienti riattivati", value: "4.000+" },
        { label: "Rating",             value: "4.9★" },
      ].map((s, i) => (
        <div key={i} className="text-center">
          <p className="text-2xl font-black text-slate-900">{s.value}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{s.label}</p>
        </div>
      ))}
    </div>
  </div>
);

export default DemoBenesserePosturale;
